<template>
  <div
    class="bj-wang-editor"
    :class="{ 'is-readonly': isReadonly }"
  >
    <div
      v-if="showToolbar && !isReadonly"
      class="bj-wang-editor__toolbar"
      @mousedown.prevent
    >
      <button
        class="bj-wang-editor__upload-button"
        type="button"
        title="Upload Image"
        @click="triggerImageSelect"
      >
        <i class="el-icon-picture-outline" />
      </button>
      <input
        ref="imageInput"
        class="bj-wang-editor__upload-input"
        type="file"
        accept="image/*"
        @change="handleImageChange"
      />
    </div>
    <Editor
      v-model="html"
      class="bj-wang-editor__editor"
      :style="editorStyle"
      :default-config="editorConfig"
      :mode="mode"
      @onCreated="handleCreated"
      @onChange="handleChange"
      @onBlur="handleBlur"
      @customPaste="handlePaste"
    />
  </div>
</template>

<script>
import { Editor } from '@wangeditor/editor-for-vue';
import '@wangeditor/editor/dist/css/style.css';
import { settingApi } from '@/api';
import { getEnvGlobalConfig } from '@/utils';

const EMPTY_HTML_LIST = ['', '<p><br></p>', '<p></p>'];

export default {
  name: 'BjWangEditor',
  components: {
    Editor,
  },
  props: {
    value: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: 'Please Enter',
    },
    height: {
      type: [Number, String],
      default: 160,
    },
    minHeight: {
      type: [Number, String],
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    showToolbar: {
      type: Boolean,
      default: true,
    },
    maxFileSize: {
      type: Number,
      default: 5 * 1024 * 1024,
    },
    query: {
      type: Object,
      default: () => {
        return {
          bucketName: getEnvGlobalConfig('VUE_APP_UPLOAD_BUCKET_NAME'),
        };
      },
    },
    resKey: {
      type: String,
      default: 'message',
    },
  },
  data() {
    return {
      editor: null,
      html: this.normalizeValue(this.value),
      mode: 'default',
      editorConfig: {
        placeholder: this.placeholder,
        autoFocus: false,
        scroll: true,
      },
    };
  },
  computed: {
    isReadonly() {
      return this.disabled || this.readonly;
    },
    editorStyle() {
      const height = typeof this.height === 'number' ? `${this.height}px` : this.height;
      const minHeight = typeof this.minHeight === 'number' ? `${this.minHeight}px` : this.minHeight;
      return {
        minHeight: minHeight || height,
      };
    },
  },
  watch: {
    value(val) {
      const nextHtml = this.normalizeValue(val);
      if (nextHtml !== this.html) {
        this.html = nextHtml;
      }
    },
    placeholder(val) {
      this.editorConfig.placeholder = val;
    },
    isReadonly() {
      this.setEditorReadonly();
    },
  },
  beforeDestroy() {
    if (this.editor) {
      this.editor.destroy();
      this.editor = null;
    }
  },
  methods: {
    handleCreated(editor) {
      this.editor = Object.seal(editor);
      this.setEditorReadonly();
    },
    handleChange(editor) {
      const html = editor.isEmpty() ? '' : editor.getHtml();
      this.$emit('input', html);
      this.$emit('change', html);
    },
    handleBlur(editor) {
      const html = editor.isEmpty() ? '' : editor.getHtml();
      this.$emit('blur', html);
    },
    triggerImageSelect() {
      if (this.isReadonly || !this.editor) return;
      if (typeof this.editor.focus === 'function') {
        this.editor.focus();
      }
      this.$refs.imageInput.click();
    },
    handleImageChange(event) {
      const files = Array.prototype.slice.call(event.target.files || []);
      files.forEach(file => {
        this.uploadAndInsertImage(file, this.insertImage);
      });
      event.target.value = '';
    },
    insertImage(url, alt, href) {
      if (!this.editor) return;
      if (typeof this.editor.focus === 'function') {
        this.editor.focus();
      }
      this.editor.insertNode({
        type: 'image',
        src: url,
        alt,
        href,
        children: [{ text: '' }],
      });
    },
    setEditorReadonly() {
      if (!this.editor) return;
      if (this.isReadonly) {
        this.editor.disable();
      } else {
        this.editor.enable();
      }
    },
    handlePaste(editor, event, callback) {
      if (this.isReadonly) {
        callback(false);
        return false;
      }

      const clipboardData = event.clipboardData || window.clipboardData;
      if (!clipboardData) {
        callback(true);
        return true;
      }

      const items = Array.prototype.slice.call(clipboardData.items || []);
      const imageFiles = items
        .filter(item => item.type && item.type.indexOf('image/') === 0)
        .map(item => item.getAsFile())
        .filter(Boolean);
      const text = clipboardData.getData('text/plain');

      if (!imageFiles.length && !text) {
        callback(true);
        return true;
      }

      event.preventDefault();
      callback(false);

      if (text) {
        editor.insertText(text);
      }

      imageFiles.forEach(file => {
        this.uploadAndInsertImage(file, this.insertImage);
      });
      return false;
    },
    async uploadAndInsertImage(file, insertFn) {
      if (!this.checkImage(file)) return;

      try {
        const url = await this.uploadFileApi(file);
        const imageUrl = this.normalizeImageUrl(url);
        insertFn(imageUrl, file.name || '', imageUrl);
      } catch (error) {
        this.$message.error(this.$t ? this.$t('UploadFailText') : 'Upload failed');
      }
    },
    checkImage(file) {
      if (!file || !file.type || file.type.indexOf('image/') !== 0) {
        this.$message.error('Only image files can be uploaded');
        return false;
      }
      if (file.size > this.maxFileSize) {
        this.$message.error(`Image cannot exceed ${Math.floor(this.maxFileSize / 1024 / 1024)}MB`);
        return false;
      }
      return true;
    },
    uploadFileApi(file) {
      return new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append('file', file);

        const API_DATA = {
          BjBody: formData,
          BjQuery: Object.assign({}, this.query, {
            fileName: Date.now() + '_' + this.randomFn() + this.getSuffix(file.name),
          }),
        };

        settingApi.uploadFiles(API_DATA)
          .then(res => {
            if (res.failed === true) {
              reject(res);
              return null;
            }
            if (!this.resKey) return res;

            return this.resKey.split('/').reduce((temp, key) => {
              return temp && temp[key];
            }, res);
          })
          .then(url => {
            if (url) {
              resolve(url);
            } else {
              reject(url);
            }
          })
          .catch(reject);
      });
    },
    normalizeImageUrl(url) {
      if (!url || typeof url !== 'string') return '';
      if (/^(https?:)?\/\//.test(url) || url.indexOf('data:') === 0 || url.indexOf('blob:') === 0) {
        return url.indexOf('//') === 0 ? `https:${url}` : url;
      }
      return `https://${url}`;
    },
    normalizeValue(value) {
      if (!value || EMPTY_HTML_LIST.includes(value)) return '';
      if (this.isHtml(value)) return value;
      return this.textToHtml(value);
    },
    isHtml(value) {
      return /<\/?[a-z][\s\S]*>/i.test(value);
    },
    textToHtml(value) {
      return String(value)
        .split(/\r?\n/)
        .map(line => `<p>${this.escapeHtml(line) || '<br>'}</p>`)
        .join('');
    },
    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    },
    getSuffix(fileName = '') {
      const pos = fileName.lastIndexOf('.');
      return pos > -1 ? fileName.substring(pos) : '';
    },
    randomFn() {
      return Number(Math.random().toString().substr(3, 12) + Date.now())
        .toString(36)
        .substr(0, 8)
        .toUpperCase();
    },
  },
};
</script>

<style lang="scss" scoped>
.bj-wang-editor {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
  background: #fff;

  &.is-readonly {
    background: #f5f7fa;
  }
}

.bj-wang-editor__toolbar {
  display: flex;
  align-items: center;
  height: 40px;
  padding: 4px 8px;
  border-bottom: 1px solid #ebeef5;
}

.bj-wang-editor__upload-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  color: #606266;
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: 2px;

  &:hover {
    color: #0f44d4;
    background: #f2f6fc;
  }
}

.bj-wang-editor__upload-input {
  display: none;
}

.bj-wang-editor__editor {
  background: #fff;
}

.bj-wang-editor.is-readonly .bj-wang-editor__editor {
  background: #f5f7fa;
}

::v-deep .w-e-text-container {
  height: auto !important;
  min-height: inherit;
  background: transparent;
}

::v-deep .w-e-text-placeholder {
  top: 8px;
  left: 12px;
  font-style: normal;
  color: #c0c4cc;
}

::v-deep .w-e-scroll {
  height: auto !important;
  min-height: inherit;
  overflow-y: visible !important;
}

::v-deep .w-e-text-container [data-slate-editor] {
  min-height: inherit;
  padding: 8px 12px;
}

::v-deep .w-e-text-container p {
  margin: 0 0 6px;
}

::v-deep .w-e-text-container img {
  max-width: 100%;
  height: auto;
}
</style>
